import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Theme } from 'ds';
import { STICKY_ROOT } from 'consts/rootNodes';

// single bordered pill containing both the search icon and the input. Confirmed via
// get_design_context on all 4 Figma states (10998:161342/161344/161405/161425): the border is
// ALWAYS grey1 (not just on hover, as the previous pass assumed) — only the background toggles
// white -> grey10 on hover/focus.
export const Wrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 48px;
  padding: 0 ${({ theme }) => theme.spacing(2)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  background: ${({ theme }) => theme.colors.neutrals.white};
  border: 1.5px solid ${({ theme }) => theme.colors.neutrals.grey1};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  transition: background-color 0.2s ease;

  &:hover, &:focus-within {
    background: ${({ theme }) => theme.colors.neutrals.grey10};
  }
`;

// "done" state (a neighbourhood is selected, field not focused) — same outer box-model as
// Wrapper, but always grey10 bg, asymmetric padding (8px on the clear-button side / 16px on the
// search-icon side, per Figma's confirmed pl-8/pr-16), and space-between so the two groups pin to
// opposite physical edges. Figma's own generated JSX is plain-LTR (ClearButton first -> physical
// left in Figma's own preview); under this app's real dir="rtl" the mirror-image DOM order
// (ValueGroup first -> physical right, ClearButton last -> physical left) reproduces the same
// visual result — see HeaderSearch.tsx.
export const DoneStateWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 48px;
  padding-inline-start: ${({ theme }) => theme.spacing(2)};
  padding-inline-end: ${({ theme }) => theme.spacing(1)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.neutrals.grey10};
  border: 1.5px solid ${({ theme }) => theme.colors.neutrals.grey1};
  border-radius: ${({ theme }) => theme.borderRadius.large};
`;

export const ValueGroup = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const ValueDivider = styled.div`
  flex-shrink: 0;
  width: 1px;
  height: 20px;
  border-radius: ${({ theme }) => theme.borderRadius.smallestSideRound};
  background: ${({ theme }) => theme.colors.neutrals.grey1};
`;

export const ValueLabel = styled.p`
  flex: 1 0 0;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: NovemberHebrew;
  font-size: 16px;
  line-height: 22px;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  text-align: right;
`;

// 32x32 rounded-8 grey button — matches the confirmed Figma asset (investors-header-clear.svg)
// exactly, so no extra background/radius styling needed here beyond sizing + reset.
export const ClearButton = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 32px;
    height: 32px;
  }
`;

// investors-header-search.svg already bakes in the correct #303030 (grey1) stroke — no color
// override needed (the previous search-thin.svg reuse hardcoded stroke="white" and needed a
// forced-color workaround; that's gone now that this is a dedicated, correctly-colored asset)
export const SearchIcon = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 20px;
    height: 20px;
  }
`;

export const AutocompleteWrapper = styled.div`
  flex: 1 0 0;
  min-width: 0;

  // the shared TextField's own input (.textfield-input) carries a 1px border + padding + fixed
  // height + rounded corners — that produced the "field inside a field" double-border look. Flatten
  // it completely so ONLY this component's outer pill (Wrapper) is the visible border, and the
  // input text sits inline right after the leading search icon like part of the placeholder
  & .textfield-input {
    border: none;
    background: transparent;
    padding: 0;
    height: auto;
    border-radius: 0;
    font-family: NovemberHebrew;
    font-size: 16px;
    line-height: 22px;
    color: ${({ theme }) => theme.colors.neutrals.grey1};

    &:hover, &:focus {
      border: none;
    }
    &::placeholder {
      color: ${({ theme }) => theme.colors.neutrals.grey3};
    }
  }

  // exact values confirmed via get_design_context on the outer panel (Figma 11156:53471): border
  // grey8 (not grey6), two-layer "Level 7" shadow (same shape as the avatar-menu dropdown),
  // asymmetric padding (16px top / 8px sides+bottom), 24px gap between the favorites/recent groups
  //
  // width/position: this dropdown's positioning ancestor is inside THIS AutocompleteWrapper
  // (flex:1, sitting after the leading search icon+gap in Wrapper), so a plain "width:100%" only
  // matches the flex:1 sub-box, not the full search pill — confirmed live (Wrapper 700px vs
  // dropdown 629px, a 68px gap = Wrapper's own left+right padding (16px+16px) + the icon (24px)
  // + the gap between icon and input (12px)). Fixed via calc() against those same constants
  // (including Wrapper's own 1.5px border, which sits outside the padding) rather than a
  // hardcoded pixel width, so it keeps tracking Wrapper's real width at any size.
  & div[data-auto="autocomplete-suggestions"] {
    inset-inline-start: calc(-1 * (1.5px + ${({ theme }) => theme.spacing(2)} + 24px + ${({ theme }) => theme.spacing(1.5)}));
    width: calc(100% + 1.5px + ${({ theme }) => theme.spacing(2)} + 24px + ${({ theme }) => theme.spacing(1.5)} + 1.5px + ${({ theme }) => theme.spacing(2)});
    // top is measured from the input wrapper (which sits ~18px below the 48px pill's top, since the
    // input line is vertically centered), so a plain "48 + 8" over-shoots. 38px lands the dropdown
    // 8px below the pill's bottom edge, per Figma.
    top: 38px;
    box-sizing: border-box;
    border: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
    box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.05), 0px 2px 5px rgba(0, 0, 0, 0.06);
    border-radius: ${({ theme }) => theme.borderRadius.large};
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(1)}`};

    // section titles (SuggestionTitleWrapper, a plain non-".autocomplete-suggestion" div sibling
    // among the row elements) default to a 32px/16px title-to-title/title-to-row margin — Figma
    // confirms 24px/8px for this instance. Scoped to this dropdown only, not a shared-component edit.
    svg path {
      fill: unset;
    }

    > div:not(.autocomplete-suggestion) {
      margin-top: ${({ theme }) => theme.spacing(3)} !important;
      margin-bottom: ${({ theme }) => theme.spacing(1)} !important;
      color: ${({ theme }) => theme.colors.neutrals.grey1};

      &:first-of-type {
        margin-top: 0 !important;
      }
    }
  }

  // taller rows to fit the new 48px leading icon box (the shared row is 40px by default). Figma
  // stacks [bold name / grey subtitle] as two lines beside the icon — the shared row
  // (SuggestionItem.tsx) renders icon+text+type as 3 flat flex siblings on one line, which can't
  // be regrouped into "icon beside a 2-line block" via flex alone. Switched to CSS Grid instead:
  // explicit placement (not auto-flow) sidesteps having to reason about RTL auto-placement order.
  // Column 1 = grid's "start" edge = physical RIGHT under this app's real dir="rtl" (confirmed via
  // the icon already rendering correctly on the right under the existing flex+RTL setup — grid
  // line 1 maps to the same physical edge). Targeted via the shared component's own stable
  // selectors (data-auto on the text elements; Emotion's babel-plugin label on the icon wrapper's
  // class, "...-IconSuggestion") — no shared-file edit, no JSX change.
  // padding confirmed via get_design_context on the actual row (11008:161898): p-[4px], not the
  // shared AddressSuggestion's own 8px (theme.spacing(1)) — overridden here, scoped to this
  // dropdown only. grid-template-rows is 1fr 1fr (not "auto auto"): with "auto", a row that has
  // no subtitle (secondaryLabel null) collapses the 2nd text row to 0 height, shrinking the
  // icon's own spanned area and pushing it off-center — 1fr 1fr keeps the icon's span pinned to
  // the full 56px row regardless of whether the subtitle renders.
  & .autocomplete-suggestion {
    height: 56px;
    padding: 0 ${({ theme }) => theme.spacing(0.5)};
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: 1fr 1fr;
    align-items: center;
    column-gap: ${({ theme }) => theme.spacing(2)};

    [data-auto="suggestion-icon"] {
      grid-column: 1;
      grid-row: 1 / span 2;
    }

    [data-auto="suggetion-text"] {
      grid-column: 2;
      grid-row: 1;
      display: block;
      font-weight: 700;
    }

    [data-auto="suggestion-type"] {
      grid-column: 2;
      grid-row: 2;
      display: block;
      padding: 0;
      color: ${({ theme }) => theme.colors.neutrals.grey2};

      &::before {
        content: none;
      }
    }
  }
`;

// the redesigned dropdown-row leading icon: a 48x48 grey (#f3f3f3) rounded box with a centered
// house glyph, replacing the bare 24px city icon (matches Figma 11008:161898). Confirmed hover
// look (11008:161900/11049:93924): bg darkens to grey8 and radius shrinks to 4px — real mouse
// :hover, not tied to Downshift's keyboard isActive state (which only drives the shared row's own
// background), so this is scoped purely via CSS, no shared-component change needed.
export const SuggestionIconBox = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.neutrals.grey9};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, border-radius 0.2s ease;

  .autocomplete-suggestion:hover & {
    background: ${({ theme }) => theme.colors.neutrals.grey8};
    border-radius: ${({ theme }) => theme.borderRadius.tiny};
  }

  // the shared AddressSuggestion (components/autocomplete/suggestions/styled.tsx) applies its own
  // "svg { margin-left: 8px }" site-wide default — same specificity as this rule, so which one wins
  // depends on emotion's own injection order (confirmed live: won on the highlighted row, lost on
  // the rest). !important makes this deterministic without editing the shared file.
  > svg {
    width: 24px;
    height: 24px;
    min-width: 24px;
    margin: 0 !important;
  }
`;

// On mobile, the shared SingleLocationAutocomplete swaps to a full-screen mode once the dropdown
// opens (Downshift isOpen + isMobileView) — confirmed via investigation: it renders the input AND
// suggestion list through a REAL DOM portal (react-dom createPortal) into `#sticky-root`, a node
// that lives outside this component's own DOM tree entirely (a sibling of #root in the SSR
// markup). All the CSS above is written as Emotion descendant selectors nested under
// AutocompleteWrapper's own class ("& .autocomplete-suggestion", "& .textfield-input", etc.) —
// those require real DOM ancestry and silently never match once content is portaled elsewhere, so
// none of it applied on mobile (confirmed live: the portaled input still had the shared TextField's
// default border/background, and rows fell back to the shared single-line "name · type" layout).
// Since #sticky-root is a plain, shared portal root (also used by a few unrelated sticky bars
// elsewhere in the app), this is intentionally scoped to id-selector + specific classes/data-auto
// only (never a bare element/tag), so it can't leak into unrelated content that happens to share
// the same root. Not the desktop card chrome (border/shadow/width) — MobileSuggestionsWrapper
// already provides its own full-screen white overlay treatment, confirmed correct via Figma
// (11164:93901: the mobile suggestions list has no card border/shadow at all).
//
// Plain css()-returning function (not a component) — this file has a .ts extension, not .tsx, so
// it can't emit JSX. The <Global styles={mobileSuggestionsGlobalStyles(theme)} /> element itself is
// rendered from HeaderSearch.tsx instead.
export const mobileSuggestionsGlobalStyles = (theme: Theme) => css`
      #${STICKY_ROOT} {
        // unlike desktop (where the outer Wrapper pill supplies the border and this input is
        // flattened to sit inline inside it), mobile has NO outer pill once portaled — Wrapper/
        // SearchIcon stay behind in the non-portaled tree. Figma's mobile "active"/"done" states
        // (11164:94176/94262) confirm the input itself IS the bordered pill here. border-color is
        // !important because the shared TextFieldWrapper (single-location-autocomplete/styled.tsx)
        // already forces border-color: grey9 !important on this same input when isMobile && isOpen
        // — TextFieldWrapper is a real DOM ancestor inside the portaled subtree, so it still reaches
        // this element and must be out-ranked. Deliberately NOT setting padding-inline-end/padding-
        // left/right here — that's the exact longhand TextFieldWrapper already toggles between its
        // default and spacing(4) to clear the showClearButton icon; a shorthand or matching longhand
        // override here would silently break that toggle.
        .textfield-input {
          background: ${theme.colors.neutrals.grey10};
          border: 2px solid ${theme.colors.neutrals.grey1} !important;
          border-radius: ${theme.borderRadius.large};
          height: 48px;
          padding-inline-start: ${theme.spacing(2)};
          font-family: NovemberHebrew;
          font-size: 16px;
          line-height: 22px;
          color: ${theme.colors.neutrals.grey1};

          &:hover, &:focus {
            border: 2px solid ${theme.colors.neutrals.grey1} !important;
          }
          &::placeholder {
            color: ${theme.colors.neutrals.grey3};
          }
        }

        // built-in shared clear-button (single-location-autocomplete's showClearButton feature,
        // enabled in HeaderSearch.tsx) — cosmetic polish only, toward Figma's 32x32 rounded-8
        // #ececec pill; the glyph itself stays the shared assets/svg/close.svg black X (re-skinning
        // it would need a shared-component change).
        [data-auto="search-clear"] {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${theme.colors.neutrals.grey8};
          border-radius: ${theme.borderRadius.small};
          // the shared ClearButton (single-location-autocomplete/styled.tsx) positions itself via
          // position:absolute; top:50%; left:spacing(1.5); margin-top:-spacing(1) — tuned for its
          // own default 16x16 box. Enlarging it to 32x32 above needs matching position overrides:
          // left tightened to 8px, and margin-top doubled to -16px so it re-centers vertically
          // against the new, taller box (half of 32px, not half of 16px).
          left: 8px;
          margin-top: -16px;

          svg {
            width: 16px;
            height: 16px;
          }
        }

        > div:not(.autocomplete-suggestion) {
          margin-top: ${theme.spacing(3)};
          margin-bottom: ${theme.spacing(1)};
          color: ${theme.colors.neutrals.grey1};

          &:first-of-type {
            margin-top: 0;
          }
        }

        // touch taps fire synthetic mouse events that drive Downshift's highlightedIndex/isActive
        // (AddressSuggestion's only background trigger — confirmed no plain CSS :hover exists on
        // it) — with no real mouse-leave on touch, a tapped row's grey highlight sticks until
        // another row is touched, reading like a stuck hover. Neutralized for mobile rows only; the
        // id-scoped selector already out-ranks the shared component's plain generated class.
        .autocomplete-suggestion {
          height: 56px;
          padding: 0 ${theme.spacing(0.5)};
          display: grid;
          grid-template-columns: auto 1fr;
          grid-template-rows: 1fr 1fr;
          align-items: center;
          column-gap: ${theme.spacing(2)};
          background: ${theme.colors.neutrals.white};

          [data-auto="suggestion-icon"] {
            grid-column: 1;
            grid-row: 1 / span 2;
          }

          [data-auto="suggetion-text"] {
            grid-column: 2;
            grid-row: 1;
            display: block;
            font-weight: 700;
          }

          [data-auto="suggestion-type"] {
            grid-column: 2;
            grid-row: 2;
            display: block;
            padding: 0;
            color: ${theme.colors.neutrals.grey2};

            &::before {
              content: none;
            }
          }
        }
      }
`;
